import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { QuestionRenderer } from '@/components/assessment-engine/QuestionRenderer';
import { AE_BUILDER_QUESTION_LABEL, AE_MUTED, AE_REQUIRED } from '@/components/assessment-engine/layout/aeTheme';
import type { AeTemplateQuestion } from '@/types/assessmentEngine';
import { BuilderActionBar } from './BuilderActionBar';
import { BuilderEntityIcon } from './BuilderEntityIcon';
import { QuestionBuilderSettings } from './QuestionBuilderSettings';
import { QuestionSettingsToggle } from './CollapsibleQuestionSettings';
import type { QuestionDraft } from './questionDraftTypes';
import {
  defaultOptionsForType,
  draftDefaultsForType,
  fromBuilderQuestionType,
  nextMatrixRowKey,
  parseDisplayConfig,
  questionUsesScale,
  slugOptionValue,
  toBuilderQuestionType,
} from './questionTypeConfig';
import type { AeTemplateScalePreset } from './templateConfigTypes';
import { DEFAULT_SCALE_MAX_LABEL, DEFAULT_SCALE_MIN_LABEL } from './scaleUtils';
import { overlayLinkedScalePreset } from './scalePresetLink';
import { useDebouncedSave } from './useDebouncedSave';
import { useTemplateSaveError } from './useTemplateSaveError';
import { hasShowCondition } from './templateStructureStats';
import { isStructureDeletePending } from './structureDeleteGuard';

export type { QuestionDraft } from './questionDraftTypes';

interface QuestionBlockProps {
  question: AeTemplateQuestion;
  questionNumber: number;
  readOnly?: boolean;
  scalePresets?: AeTemplateScalePreset[];
  onSave: (draft: QuestionDraft) => Promise<void>;
  onCopy: () => Promise<void>;
  onDelete: () => Promise<void>;
}

function draftFromQuestion(q: AeTemplateQuestion, scalePresets: AeTemplateScalePreset[] = []): QuestionDraft {
  const builderType = toBuilderQuestionType(q.question_type);
  const display = parseDisplayConfig(q.display_config);
  const typeDefaults = draftDefaultsForType(builderType);
  const display_config = { ...typeDefaults.display_config, ...display };
  // Existing templates may omit choiceDisplay (legacy radio rows). Keep radio as the default.
  if (builderType === 'single_choice' && !display_config.choiceDisplay) {
    display_config.choiceDisplay = 'radio';
  }
  if (builderType === 'multi_choice' && !display_config.choiceDisplay) {
    display_config.choiceDisplay = 'checkbox';
  }
  if (questionUsesScale(builderType)) {
    if (!display_config.scaleMinLabel?.trim()) {
      display_config.scaleMinLabel = DEFAULT_SCALE_MIN_LABEL;
    }
    if (!display_config.scaleMaxLabel?.trim()) {
      display_config.scaleMaxLabel = DEFAULT_SCALE_MAX_LABEL;
    }
  }
  const base: QuestionDraft = {
    question_text: q.question_text,
    question_type: builderType,
    is_required: q.is_required,
    scale_min: q.scale_min ?? (builderType === 'nps' ? 0 : 1),
    scale_max: q.scale_max ?? (builderType === 'nps' ? 10 : 5),
    allow_na: q.allow_na,
    display_config,
    options:
      q.options.length > 0
        ? q.options.map((o) => ({
            option_value: o.option_value,
            option_label: o.option_label,
            sort_order: o.sort_order,
          }))
        : defaultOptionsForType(builderType),
  };
  // Keep Survey design on the live shared scale when linked (or legacy-matched).
  return overlayLinkedScalePreset(base, scalePresets);
}

function isNewQuestion(q: AeTemplateQuestion, defaultLabel: string): boolean {
  const text = q.question_text.trim();
  return !text || text === defaultLabel;
}

/** Stable server snapshot — resync local draft only after reload, not every parent render. */
function questionServerRevision(q: AeTemplateQuestion): string {
  return JSON.stringify({
    id: q.id,
    question_text: q.question_text,
    question_type: q.question_type,
    is_required: q.is_required,
    scale_min: q.scale_min,
    scale_max: q.scale_max,
    allow_na: q.allow_na,
    display_config: q.display_config,
    options: q.options,
    sort_order: q.sort_order,
  });
}

/** In-situ WYSIWYG question — collapsible settings first, then question text and response options. */
export const QuestionBlock: React.FC<QuestionBlockProps> = ({
  question,
  questionNumber,
  readOnly,
  scalePresets = [],
  onSave,
  onCopy,
  onDelete,
}) => {
  const { t } = useLanguage();
  const onSaveError = useTemplateSaveError();
  const defaultQuestionLabel = t('assessmentEngine.builder.newQuestionDefault');
  const isConditional = hasShowCondition(question);
  const [draft, setDraft] = useState<QuestionDraft>(() => draftFromQuestion(question, scalePresets));
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(() => isNewQuestion(question, defaultQuestionLabel));
  const serverRevision = useMemo(() => questionServerRevision(question), [question]);
  const scalePresetsRevision = useMemo(() => JSON.stringify(scalePresets), [scalePresets]);

  useEffect(() => {
    setDraft(draftFromQuestion(question, scalePresets));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resync when server revision changes only
  }, [serverRevision]);

  // Shared-scale edits on Setup: refresh linked scale fields without wiping in-progress text edits.
  useEffect(() => {
    setDraft((prev) => overlayLinkedScalePreset(prev, scalePresets));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when presets change
  }, [scalePresetsRevision]);

  // New questions (e.g. after add) open settings so type is chosen first.
  useEffect(() => {
    if (isNewQuestion(question, defaultQuestionLabel)) {
      setSettingsOpen(true);
    }
  }, [question.id, question.question_text, defaultQuestionLabel]);

  const persist = useCallback(async () => {
    if (readOnly) return;
    // Skip saves while this question is being deleted (avoids unmount-flush races)
    if (isStructureDeletePending(question.id)) return;
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  }, [draft, onSave, readOnly, question.id]);

  useDebouncedSave(persist, [draft], 800, { onError: onSaveError });

  const patchDraft = (patch: Partial<QuestionDraft>) => {
    setDraft((prev) => {
      const next =
        patch.question_type && patch.question_type !== prev.question_type
          ? {
              ...prev,
              ...draftDefaultsForType(patch.question_type),
              // Preset-specific scale values must win over generic type defaults.
              ...patch,
              question_text: prev.question_text,
              is_required: prev.is_required,
            }
          : { ...prev, ...patch };

      // Matrix rows are the editable statements. A matrix must always expose at
      // least one statement so it can be edited and extended (e.g. when a shared
      // scale marked "matrix shared" is applied and no rows were carried over).
      if (next.question_type === 'matrix' && next.options.length === 0) {
        next.options = defaultOptionsForType('matrix');
      }

      return next;
    });
  };

  const renderQuestion = useMemo(
    () => ({
      id: question.id,
      question_key: question.question_key,
      question_text: draft.question_text,
      question_type: fromBuilderQuestionType(draft.question_type),
      sort_order: question.sort_order,
      is_required: draft.is_required,
      scale_min: draft.scale_min,
      scale_max: draft.scale_max,
      allow_na: draft.allow_na,
      display_config: draft.display_config,
      options: draft.options.map((o, i) => ({
        id: question.options[i]?.id ?? `draft-${i}`,
        option_value: o.option_value,
        option_label: o.option_label,
        sort_order: o.sort_order ?? i,
      })),
      visible: true,
    }),
    [question, draft]
  );

  const builderCallbacks = readOnly
    ? undefined
    : {
        onQuestionTextChange: (text: string) => patchDraft({ question_text: text }),
        onOptionLabelChange: (index: number, label: string) => {
          const options = draft.options.map((o, i) => {
            if (i !== index) return o;
            // Matrix rows are statement-questions: keep a stable ROW_n key and put
            // the full statement only in option_label (shared scale = column values).
            if (draft.question_type === 'matrix') {
              return { ...o, option_label: label };
            }
            return {
              ...o,
              option_label: label,
              option_value: slugOptionValue(label, o.option_value || `OPTION_${index + 1}`),
            };
          });
          patchDraft({ options });
        },
        onOptionAdd: () => {
          const n = draft.options.length + 1;
          if (draft.question_type === 'matrix') {
            const rowKey = nextMatrixRowKey(draft.options.map((o) => o.option_value));
            patchDraft({
              options: [
                ...draft.options,
                {
                  option_value: rowKey,
                  option_label: `Statement ${n}`,
                  sort_order: draft.options.length,
                },
              ],
            });
            return;
          }
          patchDraft({
            options: [
              ...draft.options,
              {
                option_value: `OPTION_${n}`,
                option_label: `Option ${n}`,
                sort_order: draft.options.length,
              },
            ],
          });
        },
        onOptionRemove: (index: number) => {
          patchDraft({
            options: draft.options
              .filter((_, i) => i !== index)
              .map((o, i) => ({ ...o, sort_order: i })),
          });
        },
      };

  return (
    <article className="w-full min-w-0 space-y-2 border-t border-[var(--color-border)] pt-4 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className={`${AE_MUTED} shrink-0`}>
          <span className={AE_BUILDER_QUESTION_LABEL}>
            <BuilderEntityIcon kind="question" />
            {t('assessmentEngine.builder.question')} {questionNumber}
          </span>
          {draft.is_required && (
            <span className={`${AE_REQUIRED} ml-1 text-base font-medium leading-none`} aria-hidden>
              *
            </span>
          )}
          {!draft.is_required && (
            <span className="ml-2 italic">({t('assessmentEngine.builder.optional')})</span>
          )}
          {isConditional && (
            <span className="ml-2 rounded bg-[var(--color-warning)]/15 px-1.5 py-0.5 text-xs text-[var(--color-warning)]">
              {t('assessmentEngine.builder.conditionalQuestion')}
            </span>
          )}
          {saving && (
            <span className="ml-2 text-xs text-[var(--color-secondary-text)]">
              {t('assessmentEngine.builder.saving')}
            </span>
          )}
        </p>

        {!readOnly && (
          <QuestionSettingsToggle
            draft={draft}
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
          />
        )}

        <div className="ml-auto shrink-0">
          <BuilderActionBar disabled={readOnly} onCopy={onCopy} onDelete={onDelete} />
        </div>
      </div>

      {!readOnly && settingsOpen && (
        <QuestionBuilderSettings draft={draft} onChange={patchDraft} scalePresets={scalePresets} />
      )}

      <div className="w-full min-w-0">
        <QuestionRenderer
        question={renderQuestion}
        value={{}}
        onChange={() => undefined}
        builderMode={!readOnly}
        builderCallbacks={builderCallbacks}
        />
      </div>
    </article>
  );
};
